import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ManualLayout, ManualSection, ManualSubheading, ManualNote, scrollToManualSection } from '@redpanda/ui';

interface RoleInfo {
  title: string;
  definition: string;
  permissions_title: string;
  permissions: string[];
}

interface UserRoles {
  title: string;
  intro: string;
  superAdmin: RoleInfo;
  admin: RoleInfo;
  member: RoleInfo;
  adminPanel: {
    title: string;
    intro: string;
    features_title: string;
    features: string[];
  };
  changeRoles: {
    title: string;
    intro: string;
    steps: string[];
  };
}

const ManualPage: React.FC = () => {
  const { t } = useTranslation('manual');
  const location = useLocation();
  const [initialScrollTarget, setInitialScrollTarget] = useState<string | null>(null);

  const sections = [
    { key: 'gettingStarted', list: true, items: ['register', 'login'] },
    { key: 'userRoles', list: false },
    { key: 'dashboard', list: false },
    { key: 'customerManagement', list: true, items: ['list', 'add', 'edit'], special: 'archive' },
    { key: 'opportunityManagement', list: true, items: ['list', 'add', 'edit', 'stages', 'tracking', 'integration', 'analysis'] },
    { key: 'quoteManagement', list: true, items: ['list', 'add', 'edit'] },
    { key: 'projectManagement', list: true, items: ['list', 'add', 'edit', 'sourceQuote'] },
    { key: 'taskManagement', list: true, items: ['list', 'add', 'edit', 'status', 'priority', 'integration'] },
    { key: 'paymentManagement', list: true, items: ['list', 'add', 'edit', 'features'] },
    { key: 'tagManagement', list: true, items: ['list', 'add', 'edit', 'initialization', 'usage', 'benefits'] },
    { key: 'reportGeneration', list: true, items: ['customerAnalysis', 'businessPerformance', 'visualization', 'realtime'] },
    { key: 'dataIO', list: true, items: ['export', 'import'] },
  ];

  // 目錄導航的章節（從 userRoles 開始）
  const tableOfContentsSections = sections.slice(1); // 排除 gettingStarted

  // 導覽系統對應的章節映射
  const navigationToSectionMap: Record<string, string> = {
    '/customers': 'customerManagement',
    '/projects': 'projectManagement', 
    '/quotes': 'quoteManagement',
    '/payments': 'paymentManagement',
    '/tasks': 'taskManagement',
    '/tags': 'tagManagement',
    '/opportunities': 'opportunityManagement',
    '/reports': 'reportGeneration'
  };

  // 在頁面載入時判斷初始滾動目標
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromSystem = urlParams.get('from');
    let targetSection = '';
    
    const hashTarget = location.hash.substring(1);

    // 優先級1: URL 查詢參數
    if (fromSystem && navigationToSectionMap[fromSystem]) {
      targetSection = navigationToSectionMap[fromSystem];
    }
    // 優先級2: 網址 #章節（分享的章節連結；點目錄時 ManualLayout 會寫入）
    else if (hashTarget && sections.some(section => section.key === hashTarget)) {
      targetSection = hashTarget;
    }
    else {
      // 優先級3: sessionStorage 中記錄的最後活動頁面
      const lastActivePage = sessionStorage.getItem('lastActivePage');
      
      // 特殊處理：如果 lastActivePage 是儀表板，跳到頂部
      if (lastActivePage === '/dashboard') {
        targetSection = '';
      }
      else if (lastActivePage && navigationToSectionMap[lastActivePage]) {
        targetSection = navigationToSectionMap[lastActivePage];
      }
      
      // 優先級4: 備用方案檢查 referrer
      if (!targetSection) {
        try {
          const referrer = document.referrer;
          
          if (referrer) {
            const referrerUrl = new URL(referrer);
            const referrerPath = referrerUrl.pathname;
            
            // 檢查是否從系統頁面進入（排除儀表板）
            if (referrerPath !== '/dashboard' && navigationToSectionMap[referrerPath]) {
              targetSection = navigationToSectionMap[referrerPath];
            } else if (referrerPath !== '/dashboard') {
              // 檢查是否從子頁面進入（排除儀表板）
              for (const [systemPath, sectionKey] of Object.entries(navigationToSectionMap)) {
                if (referrerPath.startsWith(systemPath + '/')) {
                  targetSection = sectionKey;
                  break;
                }
              }
            }
          }
        } catch (error) {
          // 忽略 referrer 解析錯誤
        }
      }
    }
    
    // 設定初始滾動目標
    setInitialScrollTarget(targetSection || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, location.hash]);

  // 在頁面內容載入後執行初始滾動
  // 章節與目錄在同一次 render 產生，effect 執行時元素已在 DOM：等下一個畫面再捲（版面已排好）即可，
  // 不需要輪詢（原本找不到章節時會每 10ms 重試、永不停止）
  useEffect(() => {
    if (!initialScrollTarget) return;
    const frame = requestAnimationFrame(() => scrollToManualSection(initialScrollTarget));
    return () => cancelAnimationFrame(frame);
  }, [initialScrollTarget]);

  const list = (items: string[] | undefined, render: (item: string) => React.ReactNode) => (
    <ul className="list-disc list-outside ml-5 space-y-1.5">
      {items?.map((item, index) => <li key={index}>{render(item)}</li>)}
    </ul>
  );

  const roleBlock = (role: RoleInfo) => (
    <div>
      <ManualSubheading>{role.title}</ManualSubheading>
      <p className="mt-2">{role.definition}</p>
      <p className="mt-3 font-medium text-fg">{role.permissions_title}</p>
      <div className="mt-2">{list(role.permissions, item => item)}</div>
    </div>
  );

  // 版面、字級、目錄由 @redpanda/ui 的 ManualLayout 提供（R26，各系統共用）
  return (
    <ManualLayout
      title={t('title')}
      tocTitle={t('tableOfContents.title')}
      toc={tableOfContentsSections.map(section => ({ id: section.key, label: t(`${section.key}.title`) }))}
    >
      {sections.map(section => {
        if (section.key === 'userRoles') {
          const userRoles = t('userRoles', { returnObjects: true }) as unknown as UserRoles;
          return (
            <ManualSection id="userRoles" key="userRoles" title={userRoles.title}>
              <p>{userRoles.intro}</p>
              {roleBlock(userRoles.superAdmin)}
              {roleBlock(userRoles.admin)}
              {roleBlock(userRoles.member)}
              <div>
                <ManualSubheading>{userRoles.adminPanel.title}</ManualSubheading>
                <p className="mt-2">{userRoles.adminPanel.intro}</p>
                <p className="mt-3 font-medium text-fg">{userRoles.adminPanel.features_title}</p>
                <div className="mt-2">{list(userRoles.adminPanel.features, item => item)}</div>
              </div>
              <div>
                <ManualSubheading>{userRoles.changeRoles.title}</ManualSubheading>
                <p className="mt-2">{userRoles.changeRoles.intro}</p>
                <ol className="list-decimal list-outside ml-5 mt-2 space-y-1.5">
                  {userRoles.changeRoles.steps.map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ol>
              </div>
            </ManualSection>
          );
        }

        if (section.key === 'gettingStarted') {
          return (
            <ManualSection id="gettingStarted" key="gettingStarted" title={t('gettingStarted.title')}>
              <p>{t('gettingStarted.content')}</p>
              {section.items?.map(item => (
                <p key={item}>{t(`gettingStarted.${item}`)}</p>
              ))}
            </ManualSection>
          );
        }

        if (section.key === 'dashboard') {
          return (
            <ManualSection id="dashboard" key="dashboard" title={t('dashboard.title')}>
              <p>{t('dashboard.content')}</p>
              <p className="font-medium text-fg">{t('dashboard.analysisIntro')}</p>
              {list(['quantityConversionRate', 'amountConversionRate', 'totalQuoteValue', 'convertedQuoteValue', 'unconvertedQuoteValue'], item => t(`dashboard.${item}`))}
            </ManualSection>
          );
        }

        if (section.key === 'customerManagement') {
          const archive = t('customerManagement.archive', { returnObjects: true }) as any;
          return (
            <ManualSection id="customerManagement" key="customerManagement" title={t('customerManagement.title')}>
              <p>{t('customerManagement.content')}</p>
              {list(section.items, item => t(`customerManagement.${item}`))}
              <div>
                <ManualSubheading>{archive.title}</ManualSubheading>
                <p className="mt-2">{archive.intro}</p>
                <p className="mt-2">{archive.purpose}</p>
                <div className="mt-4">
                  <ManualNote title={archive.quota_management?.title}>{archive.quota_management?.content}</ManualNote>
                </div>
                {[
                  [archive.features_title, archive.features],
                  [archive.access_title, archive.access],
                  [archive.restore_title, archive.restore],
                  [archive.benefits_title, archive.benefits],
                ].map(([title, items]: [string, string[]]) => (
                  <div key={title}>
                    <p className="mt-4 font-medium text-fg">{title}</p>
                    <div className="mt-2">{list(items, item => item)}</div>
                  </div>
                ))}
                <p className="mt-4 text-[13px] text-fg-muted">{archive.permissions}</p>
              </div>
            </ManualSection>
          );
        }

        return (
          <ManualSection id={section.key} key={section.key} title={t(`${section.key}.title`)}>
            <p>{t(`${section.key}.content`)}</p>
            {section.list && list(section.items, item => t(`${section.key}.${item}`))}
          </ManualSection>
        );
      })}
    </ManualLayout>
  );
};

export default ManualPage;
